import { Keyboard, Pagination } from 'swiper/modules';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { TEXT_HIDDEN, breakpoint } from 'utility/constant';
import { Tooltip, TooltipPosition } from 'components/tooltip';
import { addDays, format } from 'date-fns';
import {
  selectBonusCash,
  selectIsSaldoVisible,
  selectSaldo,
  selectSaldoNonPrelevabile,
  selectSaldoPrelevabile,
  selectWalletFetchStatus,
} from 'features/dashboard/selectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { AnchorLink } from 'components/anchorLink';
import { BonusOverview } from 'components/bonusOverview';
import { Button } from 'components/button';
import { DatoMenuItemBlock } from 'lib/datoCms/types';
import { IlMioSaldoEmptyState } from './ilMioSaldoEmptyState';
import { IlMioSaldoListItem } from './ilMioSaldoListItem';
import { InternalMenu } from 'components/internalMenu';
import { MappedBonusType } from 'types/dashboard/mappedBonusType';
import { MyAccountCms } from 'lib/datoCms/queries/getMyAccountPage';
import { MyBonusCms } from 'lib/datoCms/queries/getMyBonusPage';
import { PieChartComponent } from 'components/pieChartComponent';
import { SaldoHeader } from 'components/saldoHeader';
import { SkeletonArea } from 'components/skeletonArea';
import { getAmount } from 'utility/functions';
import { getWallet } from 'features/dashboard/action';
import styles from './IlMioSaldo.module.scss';
import { useGetBonusQuery } from 'features/api/userSlice';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks';
import { useRouter } from 'next/router';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export type IlMioSaldoProps = {
  items?: Array<DatoMenuItemBlock>;
  style?: object;
  myAccountPage: MyAccountCms;
  myBonusPage: MyBonusCms;
  containerClassName?: string;
};

export const IlMioSaldo = ({ items, style, myAccountPage, myBonusPage, containerClassName }: IlMioSaldoProps) => {
  /**
   * STATE
   */

  const [saldo, setSaldo] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<any>(items && items[0]?.id);
  const [saldoVerticali, setSaldoVerticali] = useState<any>({});
  const [selectedBalance, setSelectedBalance] = useState<any>({});

  const { width, doCheck } = useWindowSize();
  const [isMobile, isUltraWide] = doCheck([
    { size: breakpoint.md, comparer: enComparer.less },
    { size: breakpoint.xl, comparer: enComparer.greater | enComparer.equal },
  ]);

  const labelPrelevabile = useGetLabelByKey('prelevabile');
  const labelNonPrelevabile = useGetLabelByKey('non-prelevabile');
  const labelBonusCash = useGetLabelByKey('bonus-cash');
  const labelVaiAlConto = useGetLabelByKey('vai-al-conto');
  const labelAggiornato = useGetLabelByKey('aggiornato-alle');
  const labelTimeout = useGetLabelByKey('generic-timeout-error');
  const labelRicarica = useGetLabelByKey('ricarica');
  const labelTooltipSpendibileSu = useGetLabelByKey('tooltip-spendibile-su-il-mio-saldo');

  const { isAuthenticated } = useInternalSession();
  const dispatch = useAppDispatch();

  const { pathname } = useRouter();
  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  });

  const isMyAccountPage =
    pathname.includes('my-account') || pathname.includes('il-mio-conto') || pathname.includes('seamless-login');

  const selectedItem = useMemo(() => {
    const item = items && items.find((item) => item.id === selectedTab);
    const slug = item && item.linkBlock?.length > 0 && item.linkBlock[0].href.replace('/dashboard/i-miei-bonus/', '');
    return !!slug ? slug?.charAt(0).toUpperCase() + slug?.slice(1) : 'Cash';
  }, [selectedTab, items]);

  const {
    data: myBonusData,
    isLoading: myBonusLoading,
    isFetching: myBonusIsFetching,
    // isError: myBonusIsError,
    error: myBonusError,
    refetch: refetchMyBonus,
  } = useGetBonusQuery(
    {
      type: selectedItem,
      filterVertical: undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: bonusResponse,
    // isError: bonusResponseError,
    error: bonusResponseErrorObject,
    isLoading: bonusResponseLoading,
    isFetching: bonusResponseIsFetching,
    refetch: refetchBonusCash,
  } = useGetBonusQuery(
    {
      type: 'Cash',
      filterVertical: undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const bonusCash = useTypedSelector(selectBonusCash);
  const saldoTotale = useTypedSelector(selectSaldo);
  const isSaldoVisible = useTypedSelector(selectIsSaldoVisible);
  const saldoPrelevabile = useTypedSelector(selectSaldoPrelevabile);
  const saldoNonPrelevabile = useTypedSelector(selectSaldoNonPrelevabile);
  const { walletUpdated, isWalletLoading, isWalletUninitialized } = useTypedSelector(selectWalletFetchStatus);

  const lastUpdate = useMemo(() => {
    return walletUpdated ? format(new Date(walletUpdated), 'HH:mm:ss') : '';
  }, [walletUpdated]);

  const isErrorTimeoutBonusCash = useMemo(() => {
    const error = bonusResponseErrorObject as { name: string; message: string } | undefined;
    return error?.name === 'AbortError';
  }, [bonusResponseErrorObject]);

  const isErrorTimeoutBonus = useMemo(() => {
    const error = myBonusError as { name: string; message: string } | undefined;
    return error?.name === 'AbortError';
  }, [myBonusError]);

  const refetchWallet = useCallback(() => {
    dispatch(getWallet({ ts: new Date() }));
    refetchBonusCash();
  }, [dispatch, refetchBonusCash]);

  const refetchBonus = () => {
    refetchMyBonus();
  };

  useEffect(() => {
    if (isAuthenticated && (isWalletUninitialized || !saldoPrelevabile)) {
      refetchWallet();
    }
  }, [isAuthenticated, isWalletUninitialized, saldoPrelevabile, refetchWallet]);

  useEffect(() => {
    setSaldo({
      saldoTotale,
      chartData: [
        {
          name: labelPrelevabile,
          value: saldoPrelevabile,
        },
        {
          name: labelNonPrelevabile,
          value: saldoNonPrelevabile,
        },
        {
          name: labelBonusCash,
          value: bonusCash,
        },
      ],
    });
  }, [
    labelPrelevabile,
    labelNonPrelevabile,
    labelBonusCash,
    saldoTotale,
    saldoPrelevabile,
    saldoNonPrelevabile,
    bonusCash,
  ]);

  const filteredBonusResponse: MappedBonusType = useMemo(() => {
    const today = new Date();
    if (!myBonusData)
      return {
        listBonusCash: [],
        listBonusGold: [],
        listFreespin: [],
        listFunbonus: [],
        filtroVerticale: [],
      };
    return {
      ...myBonusData,
      listBonusCash: myBonusData?.listBonusCash?.filter((item) => {
        return item.dataTermine ? today <= addDays(new Date(item.dataTermine), 15) : item;
      }),
      listBonusGold: myBonusData?.listBonusGold?.filter((item) => {
        return item.data_scadenza ? today <= addDays(new Date(item.data_scadenza), 15) : item;
      }),
      listFreespin: myBonusData?.listFreespin?.filter((item) => {
        return item.expirationDate ? today <= addDays(new Date(item.expirationDate), 15) : item;
      }),
      listFunbonus: myBonusData?.listFunbonus?.filter((item) => {
        return item.expirationDate ? today <= addDays(new Date(item.expirationDate), 15) : item;
      }),
    };
  }, [myBonusData]);

  const filteredBonusCash = useMemo(() => {
    const today = new Date();

    return {
      ...bonusResponse,
      listBonusCash: bonusResponse?.listBonusCash?.filter((item) => {
        return item.dataTermine ? today <= new Date(item.dataTermine) : item;
      }),
    };
  }, [bonusResponse]);

  const type = useMemo<string | undefined>(() => {
    const item = items?.find((tab) => tab.id === selectedTab);
    const type =
      item && item.linkBlock && item.linkBlock.length > 0
        ? item.linkBlock[0].href.split('/')[item.linkBlock[0].href.split('/').length - 1]
        : undefined;
    return type;
  }, [items, selectedTab]);

  useEffect(() => {
    // calculate all the residous bonus cash to display
    const totalSaldoResiduoBonusCash =
      filteredBonusCash.listBonusCash?.reduce((sum, bonus) => bonus.saldoResiduo && sum + +bonus.saldoResiduo, 0) || 0;
    // calculate all the residous bonus gold to display
    const totalSaldoResiduoBonusGold =
      filteredBonusResponse.listBonusGold.reduce((sum, bonus) => bonus.impo_bonus_w && sum + +bonus.impo_bonus_w, 0) ||
      0;
    // calculate all the residous funbonus to display
    const totalSaldoResiduoFreespin =
      filteredBonusResponse.listFreespin.reduce((sum, bonus) => bonus.amount && sum + +bonus.amount, 0) || 0;
    // calculate all the residous funbonus to display
    const totalSaldoResiduoFunbonus =
      filteredBonusResponse.listFunbonus.reduce((sum, bonus) => bonus.amount && sum + +bonus.amount, 0) || 0;
    setSelectedBalance({
      cash: totalSaldoResiduoBonusCash,
      gold: totalSaldoResiduoBonusGold,
      freespin: totalSaldoResiduoFreespin,
      funbonus: totalSaldoResiduoFunbonus,
    });

    const saldoTotale = totalSaldoResiduoBonusCash;
    //SECOND SLIDE CHART DATA GENERATION
    //total by verticals of BonusCash
    let arrTotaliVerticaliBonusCash: Array<{
      name: string;
      value: number | null | undefined;
    }> = [];

    // const arrAltro: Array<string> = ['Bingo', 'Poker', 'Lotterie', 'Skill Game'];
    filteredBonusCash.filtroVerticale &&
      filteredBonusCash.filtroVerticale.length > 0 &&
      filteredBonusCash.filtroVerticale?.map((el, index) => {
        arrTotaliVerticaliBonusCash[index] = {
          name: el,
          value: filteredBonusCash.listBonusCash?.reduce((sum, bonus) => {
            return bonus.verticali.includes(el) ? bonus.saldoResiduo && sum + bonus?.saldoResiduo : sum;
          }, 0),
        };
      });
    const altroIndex = arrTotaliVerticaliBonusCash.findIndex((item) => item.name === 'Altro');
    if (arrTotaliVerticaliBonusCash.length > 0) {
      const elementoAltro = arrTotaliVerticaliBonusCash.splice(altroIndex, 1)[0];
      arrTotaliVerticaliBonusCash.push(elementoAltro);
    }

    setSaldoVerticali({
      saldoTotale,
      chartData: arrTotaliVerticaliBonusCash,
    });
  }, [filteredBonusResponse, filteredBonusCash]);

  useEffect(() => {
    if (filteredBonusResponse.listBonusCash?.length === 0) {
      if (filteredBonusResponse.listBonusGold?.length > 0) items && setSelectedTab(items[1]?.id);
      else if (filteredBonusResponse.listFreespin?.length > 0) items && setSelectedTab(items[2]?.id);
      else if (filteredBonusResponse.listFunbonus?.length > 0) items && setSelectedTab(items[3]?.id);
    }
  }, [filteredBonusResponse, items]);

  /**
   * FUNCTIONS
   */
  const handleSelectedTabChange = (id: string) => {
    setSelectedTab(id);
  };

  return (
    <div
      style={style}
      className={`${styles.container} ${containerClassName ? containerClassName : ''} ${
        isMyAccountPage ? styles.myAccount : ''
      }`}
    >
      {isWalletLoading || bonusResponseLoading || bonusResponseIsFetching ? (
        <div className={styles.swiperWrapper}>
          <SaldoHeader refetchWallet={refetchWallet} />
          <SkeletonArea height="32.5" internalClassName="noMargin" />
        </div>
      ) : isErrorTimeoutBonusCash ? (
        <div className={styles.swiperWrapper}>
          <SaldoHeader refetchWallet={refetchWallet} />
          <div className={styles.errorContainer}>
            <p>{labelTimeout}</p>
            <Button size="medium" visualizationType="secondary" onClick={() => refetchWallet()}>
              {labelRicarica}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.swiperWrapper}>
          <SaldoHeader refetchWallet={refetchWallet} />
          {!isUltraWide ? (
            <React.Fragment>
              <Swiper
                slidesPerView={1}
                grabCursor={true}
                updateOnWindowResize={true}
                watchOverflow={true}
                keyboard={{
                  enabled: true,
                }}
                className={styles.saldoSwiper}
                pagination={{
                  clickable: true,
                  renderBullet: (index, className) => {
                    return '<span class="' + styles.bulletBanner + ' ' + className + '">' + '</span>';
                  },
                }}
                style={{ width: `${width}px` }}
                modules={[Keyboard, Pagination]}
              >
                <SwiperSlide>
                  {saldo && (
                    <PieChartComponent
                      dataSrc={saldo?.chartData}
                      currentBalance={saldo?.saldoTotale}
                      lastUpdate={lastUpdate}
                      btnLabel={labelVaiAlConto}
                      btnLink={myAccountPage.href}
                      doubleColumnLegend={isMobile}
                    />
                  )}
                </SwiperSlide>
                <SwiperSlide>
                  <div
                    className={`${styles.secondSlide} ${
                      saldoVerticali && saldoVerticali?.saldoTotale === 0 ? styles.zeroCredit : ''
                    }`}
                  >
                    <div className={styles.bonusTextContainer}>
                      <p className={styles.bonusParagraph}>Bonus cash</p>
                      <ul className={styles.bonusList}>
                        <li>
                          {isSaldoVisible ? formatter.format(getAmount(saldoVerticali?.saldoTotale)) : TEXT_HIDDEN}
                        </li>
                        <li>{`${labelAggiornato} ${lastUpdate}`}</li>
                      </ul>
                    </div>
                    {saldoVerticali && saldoVerticali?.saldoTotale !== 0 ? (
                      <div className={styles.saldoContainer}>
                        <p className={styles.saldoText}>
                          <span>Spendibile su</span>
                          <Tooltip
                            content={labelTooltipSpendibileSu}
                            iconColor={'--color-on-bg-secondary'}
                            position={TooltipPosition.TOP_CENTER}
                          />
                        </p>
                        <div className={styles.saldoList}>
                          {saldoVerticali?.chartData?.map((item, index) => {
                            const { name, value } = item;
                            return (
                              <React.Fragment key={item.name + '_' + index}>
                                <IlMioSaldoListItem
                                  name={name}
                                  value={value}
                                  index={index}
                                  containerClassName={styles.saldoListItem}
                                />
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <IlMioSaldoEmptyState />
                    )}
                  </div>
                </SwiperSlide>
              </Swiper>
              {!isMyAccountPage && isMobile && (
                <div className={styles.contoWrapper}>
                  <AnchorLink className={styles.contoBtn} href={myAccountPage.href}>
                    {labelVaiAlConto}
                  </AnchorLink>
                </div>
              )}
            </React.Fragment>
          ) : (
            <div className={styles.wideChartsContainer}>
              {saldo && (
                <div>
                  <PieChartComponent
                    dataSrc={saldo?.chartData}
                    currentBalance={saldo?.saldoTotale}
                    lastUpdate={lastUpdate}
                    btnLabel={labelVaiAlConto}
                    btnLink={myAccountPage.href}
                  />
                </div>
              )}

              <div className={styles.columnDx}>
                <div className={styles.bonusTextContainer}>
                  <p className={styles.bonusParagraph}>Bonus cash</p>
                  <ul className={styles.bonusList}>
                    <li>{formatter.format(getAmount(saldoVerticali?.saldoTotale))}</li>
                    <li>{`${labelAggiornato} ${lastUpdate}`}</li>
                  </ul>
                </div>
                {saldoVerticali && saldoVerticali.chartData?.length > 0 ? (
                  <div className={styles.saldoContainer}>
                    <p className={styles.saldoText}>
                      <span>Spendibile su</span>
                      <Tooltip
                        content={labelTooltipSpendibileSu}
                        iconColor={'--color-on-bg-secondary'}
                        position={TooltipPosition.TOP_CENTER}
                      />
                    </p>
                    <div className={styles.saldoList}>
                      {saldoVerticali?.chartData?.map((item, index) => {
                        const { name, value } = item;
                        return (
                          <React.Fragment key={item.name + '_' + index}>
                            <IlMioSaldoListItem
                              name={name}
                              value={value}
                              index={index}
                              containerClassName={styles.saldoListItem}
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <IlMioSaldoEmptyState />
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* NOTE Bonus  */}
      <div className={styles.bonusWrapper}>
        {/*  <SaldoBonus bonusCategories={bonusCategories}/> */}

        {items && (
          <React.Fragment>
            <InternalMenu
              noPaddingList={false}
              noPaddingTop={true}
              noPaddingLeft={true}
              noPaddingRight={true}
              items={items}
              itemSelected={selectedTab}
              isLocalStateInternalMenu={true}
              itemSelectedCallback={handleSelectedTabChange}
              smallFont={true}
            />
            {myBonusLoading || myBonusIsFetching ? (
              <SkeletonArea height="32.5" internalClassName="noMargin" />
            ) : isErrorTimeoutBonus ? (
              <div className={styles.errorContainer}>
                <p>{labelTimeout}</p>
                <Button size="medium" visualizationType="secondary" onClick={() => refetchBonus()}>
                  {labelRicarica}
                </Button>
              </div>
            ) : (
              <BonusOverview
                myBonusPage={myBonusPage}
                bonusResponse={filteredBonusResponse}
                selectedTab={selectedTab}
                selectedBalance={type ? selectedBalance[type] : undefined}
                tabs={items}
              />
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
